import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../contexts/AuthContext';
import { Trophy, Users, Download, Loader2, Search, Play, Square, PartyPopper, CheckCircle, MapPin, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCompetition = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({});
  const [status, setStatus] = useState(null);
  const [lastWinner, setLastWinner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [drawDialog, setDrawDialog] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [winner, setWinner] = useState(null);
  const [displayConsent, setDisplayConsent] = useState(true);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [toggleDialog, setToggleDialog] = useState(false);
  const [compTitle, setCompTitle] = useState('Wettbewerb - 1 Woche GRATIS Werbefläche!');
  const [compDescription, setCompDescription] = useState('');
  const [toggling, setToggling] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [entriesRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/competition/entries`, { headers }),
        fetch(`${API_URL}/api/competition/status`)
      ]);
      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setEntries(data.entries);
        setStats(data.stats);
        setLastWinner(data.last_winner || null);
      }
      if (statusRes.ok) {
        const s = await statusRes.json();
        setStatus(s);
        setCompTitle(s.title || '');
        setCompDescription(s.description || '');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleCompetition = async (active) => {
    setToggling(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/competition/toggle`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ active, title: compTitle, description: compDescription })
      });
      if (res.ok) {
        toast.success(active ? 'Wettbewerb aktiviert' : 'Wettbewerb deaktiviert');
        fetchAll();
      }
    } catch (e) {
      toast.error('Fehler');
    } finally {
      setToggling(false);
      setToggleDialog(false);
    }
  };

  const drawWinner = async () => {
    setDrawing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/competition/draw-winner`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ display_consent: displayConsent })
      });
      const data = await res.json();
      if (res.ok) {
        setWinner(data.winner);
        toast.success(data.message);
        fetchAll();
      } else {
        toast.error(data.detail || 'Fehler beim Ziehen');
        setDrawDialog(false);
      }
    } catch (e) {
      toast.error('Fehler');
    } finally {
      setDrawing(false);
    }
  };

  const resetCompetition = async () => {
    setResetting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/competition/reset`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchAll();
      } else {
        toast.error(data.detail || 'Fehler');
      }
    } catch (e) {
      toast.error('Fehler');
    } finally {
      setResetting(false);
      setResetDialog(false);
    }
  };

  const exportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/competition/export`, { headers });
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wettbewerb_teilnehmer_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${data.total} Einträge exportiert`);
      }
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const filtered = entries.filter(e =>
    `${e.first_name} ${e.last_name} ${e.email} ${e.city}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">Wettbewerb</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Teilnehmer verwalten und Gewinner ziehen</p>
        </div>
        <div className="flex items-center gap-3">
          {status?.active ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 px-3 py-1">
              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Aktiv
            </Badge>
          ) : (
            <Badge variant="outline" className="px-3 py-1">Inaktiv</Badge>
          )}
          <Button
            variant={status?.active ? "destructive" : "default"}
            onClick={() => setToggleDialog(true)}
            data-testid="comp-toggle-btn"
          >
            {status?.active ? <><Square className="w-4 h-4 mr-2" /> Stoppen</> : <><Play className="w-4 h-4 mr-2" /> Starten</>}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-[hsl(var(--primary))]" />
              <div>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Teilnehmer</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{stats.main_winners || 0}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Hauptgewinner</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats.newsletter_subscribers || 0}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Newsletter</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary)/0.1)] flex items-center justify-center">
                <span className="text-sm font-bold text-[hsl(var(--primary))]">{Math.min(100, stats.total || 0)}%</span>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total >= 100 ? 'Bereit!' : `${100 - (stats.total || 0)}`}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">{stats.total >= 100 ? 'Auto-Verlosung' : 'bis Verlosung'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Last Winner Info */}
      {lastWinner && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <Trophy className="w-6 h-6 text-amber-500" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Letzter Gewinner: <strong>{lastWinner.name}</strong> aus {lastWinner.city}
                  {lastWinner.auto_drawn && <Badge variant="outline" className="ml-2 text-xs">Auto</Badge>}
                  {lastWinner.display_consent && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-xs">Öffentlich</Badge>}
                </p>
                <p className="text-xs text-amber-600">Gezogen am: {new Date(lastWinner.drawn_at).toLocaleDateString('de-CH')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setDrawDialog(true)} disabled={!stats.total} data-testid="comp-draw-btn">
          <Trophy className="w-4 h-4 mr-2" /> Gewinner ziehen
        </Button>
        <Button variant="outline" onClick={exportCSV} disabled={!stats.total} data-testid="comp-export-btn">
          <Download className="w-4 h-4 mr-2" /> CSV Export
        </Button>
        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setResetDialog(true)} disabled={!stats.total} data-testid="comp-reset-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Zurücksetzen
        </Button>
      </div>

      {/* Search & Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Teilnehmerliste</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="comp-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-[hsl(var(--muted-foreground))]">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Noch keine Teilnehmer</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))]">
                    <th className="text-left py-3 px-3 font-medium text-[hsl(var(--muted-foreground))]">Nr.</th>
                    <th className="text-left py-3 px-3 font-medium text-[hsl(var(--muted-foreground))]">Name</th>
                    <th className="text-left py-3 px-3 font-medium text-[hsl(var(--muted-foreground))]">E-Mail</th>
                    <th className="text-left py-3 px-3 font-medium text-[hsl(var(--muted-foreground))]">Mobile</th>
                    <th className="text-left py-3 px-3 font-medium text-[hsl(var(--muted-foreground))]">Wohnort</th>
                    <th className="text-left py-3 px-3 font-medium text-[hsl(var(--muted-foreground))]">Status</th>
                    <th className="text-left py-3 px-3 font-medium text-[hsl(var(--muted-foreground))]">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry) => (
                    <tr key={entry.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--accent)/0.3)]">
                      <td className="py-3 px-3 font-mono font-medium">{entry.entry_number}</td>
                      <td className="py-3 px-3">{entry.first_name} {entry.last_name}</td>
                      <td className="py-3 px-3 text-[hsl(var(--muted-foreground))]">{entry.email}</td>
                      <td className="py-3 px-3 text-[hsl(var(--muted-foreground))]">{entry.mobile}</td>
                      <td className="py-3 px-3">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {entry.city}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex gap-1">
                          {entry.is_main_winner && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              <Trophy className="w-3 h-3 mr-1" /> Gewinner
                            </Badge>
                          )}
                          {entry.newsletter_confirmed && (
                            <Badge variant="outline" className="text-xs">Newsletter</Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-[hsl(var(--muted-foreground))] text-xs">
                        {new Date(entry.created_at).toLocaleDateString('de-CH')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Toggle Dialog */}
      <Dialog open={toggleDialog} onOpenChange={setToggleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{status?.active ? 'Wettbewerb stoppen' : 'Wettbewerb starten'}</DialogTitle>
            <DialogDescription>
              {status?.active
                ? 'Möchten Sie den Wettbewerb wirklich stoppen? Neue Anmeldungen sind dann nicht mehr möglich.'
                : 'Konfigurieren Sie den Wettbewerb und starten Sie ihn.'}
            </DialogDescription>
          </DialogHeader>
          {!status?.active && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={compTitle}
                  onChange={(e) => setCompTitle(e.target.value)}
                  data-testid="comp-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Textarea
                  value={compDescription}
                  onChange={(e) => setCompDescription(e.target.value)}
                  rows={3}
                  placeholder="Beschreibung des Wettbewerbs..."
                  data-testid="comp-desc-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setToggleDialog(false)}>Abbrechen</Button>
            <Button
              variant={status?.active ? "destructive" : "default"}
              onClick={() => toggleCompetition(!status?.active)}
              disabled={toggling}
              data-testid="comp-toggle-confirm"
            >
              {toggling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {status?.active ? 'Stoppen' : 'Starten'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Draw Winner Dialog */}
      <Dialog open={drawDialog} onOpenChange={(open) => { setDrawDialog(open); if (!open) setWinner(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hauptgewinner ziehen</DialogTitle>
            <DialogDescription>
              {winner
                ? 'Der Gewinner wurde gezogen!'
                : `Aus ${stats.total || 0} Teilnehmern wird zufällig ein Hauptgewinner gezogen. Preis: 1 Woche GRATIS Werbefläche.`}
            </DialogDescription>
          </DialogHeader>
          {winner ? (
            <div className="text-center py-6 space-y-3">
              <PartyPopper className="w-12 h-12 text-amber-500 mx-auto" />
              <h3 className="text-xl font-bold">{winner.first_name} {winner.last_name}</h3>
              <p className="text-[hsl(var(--muted-foreground))]">{winner.email}</p>
              <p className="flex items-center justify-center gap-1 text-sm">
                <MapPin className="w-4 h-4" /> {winner.city}
              </p>
              <Badge className="bg-amber-100 text-amber-700 text-base px-4 py-1">
                Teilnehmer Nr. {winner.entry_number}
              </Badge>
              {displayConsent && (
                <p className="text-xs text-emerald-600 mt-2">Der Name wird im Popup angezeigt.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg border border-[hsl(var(--border))]">
                <Checkbox
                  id="display_consent"
                  checked={displayConsent}
                  onCheckedChange={(checked) => setDisplayConsent(!!checked)}
                  data-testid="comp-consent-checkbox"
                />
                <div>
                  <Label htmlFor="display_consent" className="text-sm font-medium cursor-pointer">
                    Gewinnername öffentlich anzeigen
                  </Label>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    Der Name des Gewinners wird im Wettbewerb-Popup angezeigt (mit Einverständnis).
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDrawDialog(false)}>Abbrechen</Button>
                <Button onClick={drawWinner} disabled={drawing} data-testid="comp-draw-confirm">
                  {drawing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
                  Gewinner ziehen
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wettbewerb zurücksetzen</DialogTitle>
            <DialogDescription>
              Alle {stats.total || 0} Teilnehmer werden gelöscht und der Zähler auf 0 zurückgesetzt.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Achtung: Diese Aktion kann nicht rückgängig gemacht werden!</p>
              <p className="text-xs text-red-600 mt-1">Alle Teilnehmerdaten werden unwiderruflich gelöscht. Der letzte Gewinner bleibt im Popup sichtbar.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(false)}>Abbrechen</Button>
            <Button
              variant="destructive"
              onClick={resetCompetition}
              disabled={resetting}
              data-testid="comp-reset-confirm"
            >
              {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Zurücksetzen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCompetition;
